import { VolumeDown, VolumeUp } from '@mui/icons-material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DeleteIcon from '@mui/icons-material/Delete';
import DevicesIcon from '@mui/icons-material/Devices';
import EditIcon from '@mui/icons-material/Edit';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Snackbar,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageTransition from '../components/PageTransition';
import { useAuth } from '../contexts/AuthContext';
import { Card, cardService } from '../services/cardService';
import { Device, deviceService } from '../services/deviceService';
import SpotifyService from '../services/spotifyService';

const UserDashboard = () => {
  const navigate = useNavigate();
  const { user, accessToken } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ [key: string]: string } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);
  const [newUrl, setNewUrl] = useState('');
  const [spotifyCovers, setSpotifyCovers] = useState<Record<string, string>>({});
  const [isSpotifyConnected, setIsSpotifyConnected] = useState(false);
  const [deviceGuideOpen, setDeviceGuideOpen] = useState(false);
  const [deleteDeviceDialogOpen, setDeleteDeviceDialogOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedDeviceConfig, setSelectedDeviceConfig] = useState<Device | null>(null);
  const [volume, setVolume] = useState(50);
  const [spotifyDevice, setSpotifyDevice] = useState('');
  const [availableSpotifyDevices, setAvailableSpotifyDevices] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [isTimeRestricted, setIsTimeRestricted] = useState(false);
  const [startTime, setStartTime] = useState<dayjs.Dayjs | null>(dayjs().hour(8).minute(0));
  const [endTime, setEndTime] = useState<dayjs.Dayjs | null>(dayjs().hour(20).minute(0));
  const [deviceNickname, setDeviceNickname] = useState('');

  const fetchDevices = async () => {
    try {
      const devicesData = await deviceService.getMyDevices();
      setDevices(devicesData);
    } catch (err) {
      console.error('Erreur lors de la récupération des appareils:', err);
      setError({ [Date.now()]: 'Une erreur est survenue lors de la récupération des appareils' });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Vérifier si l'utilisateur est connecté à Spotify
    setIsSpotifyConnected(SpotifyService.isConnectedToSpotify());
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [devicesData, cardsData] = await Promise.all([
          deviceService.getMyDevices(),
          cardService.getMyCards(),
        ]);

        setDevices(devicesData);
        const filteredCards = cardsData.filter(
          (card: Card) => card.CustomerId === Number(user?.id)
        );
        setCards(filteredCards);

        const coverPromises = filteredCards.map(async (card) => {
          if (card.url && card.url.includes(':')) {
            const cover = await SpotifyService.getThumbnailSpotify(card.url);
            return { id: card.id, coverUrl: cover };
          }
          return null;
        });
        const covers = await Promise.all(coverPromises);
        const coversMap = covers.reduce((acc, cover) => {
          if (cover) {
            acc[cover.id] = cover.coverUrl;
          }
          return acc;
        }, {} as Record<string, string>);
        setSpotifyCovers(coversMap);
      } catch (err) {
        console.error('Erreur lors de la récupération des données:', err);
        setError({ [Date.now()]: 'Une erreur est survenue lors de la récupération des données' });
      } finally {
        setLoading(false);
      }
    };

    console.log('fetchData');
    fetchData();
  }, []);

  useEffect(() => {
    if (error) {
      setSnackbarOpen(true);
      const timeout = setTimeout(() => {
        setSnackbarOpen(false);
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [error]);

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const formatLastSeen = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return "À l'instant";
    if (diffInMinutes < 60) return `Il y a ${diffInMinutes} minutes`;
    if (diffInMinutes < 1440) return `Il y a ${Math.floor(diffInMinutes / 60)} heures`;
    return `Il y a ${Math.floor(diffInMinutes / 1440)} jours`;
  };

  const getSpotifyUrl = (url: string) => {
    if (!url || !url.includes(':')) return url;
    const [type, id] = url.split(':');
    return `https://open.spotify.com/${type}/${id}`;
  };

  const handleEditClick = (card: Card) => {
    setSelectedCard(card);
    setNewUrl(card.url);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCard) return;

    try {
      const updatedCard = await cardService.updateCard(selectedCard.rfid_id, { url: newUrl });
      setCards(cards.map((card) => (card.id === updatedCard.id ? updatedCard : card)));

      // Mettre à jour la cover Spotify si nécessaire
      if (updatedCard.url && updatedCard.url.includes(':')) {
        try {
          const content = await SpotifyService.getThumbnailSpotify(updatedCard.url);
          setSpotifyCovers((prev) => ({
            ...prev,
            [updatedCard.id]: content || '',
          }));
        } catch (err) {
          console.error(
            `Erreur lors de la récupération de la cover pour la carte ${updatedCard.id}:`,
            err
          );
        }
      }

      setEditDialogOpen(false);
    } catch (err) {
      console.error('Erreur lors de la mise à jour de la carte:', err);
      setError({ [Date.now()]: 'Une erreur est survenue lors de la mise à jour de la carte' });
    }
  };

  const handleConnectSpotify = () => {
    SpotifyService.initiateLogin('web');
  };

  const handleDeleteDeviceClick = (device: Device) => {
    setSelectedDevice(device);
    setDeleteDeviceDialogOpen(true);
  };

  const handleDeleteDevice = async () => {
    if (!selectedDevice) return;

    try {
      await deviceService.deleteDevice(selectedDevice.id);
      setDevices(devices.filter((device) => device.id !== selectedDevice.id));
      setDeleteDeviceDialogOpen(false);
    } catch (err) {
      console.error("Erreur lors de la suppression de l'appareil:", err);
      setError({ [Date.now()]: "Une erreur est survenue lors de la suppression de l'appareil" });
    }
  };

  const handleOpenConfig = async (device: Device) => {
    setSelectedDeviceConfig(device);
    setSelectedDevice(device);
    setVolume(device.volume || 50);
    setDeviceNickname(device.nickname || '');
    setConfigDialogOpen(true);

    // Initialiser les créneaux horaires
    if (device.TimeSlot) {
      setIsTimeRestricted(device.TimeSlot.enabled);
      if (device.TimeSlot.start_time) {
        const [hours, minutes] = device.TimeSlot.start_time.split(':');
        setStartTime(dayjs().hour(parseInt(hours)).minute(parseInt(minutes)));
      }
      if (device.TimeSlot.end_time) {
        const [hours, minutes] = device.TimeSlot.end_time.split(':');
        setEndTime(dayjs().hour(parseInt(hours)).minute(parseInt(minutes)));
      }
    } else {
      setIsTimeRestricted(false);
      setStartTime(dayjs().hour(8).minute(0));
      setEndTime(dayjs().hour(20).minute(0));
    }

    // Récupérer les appareils Spotify si l'utilisateur est connecté
    if (SpotifyService.isConnectedToSpotify()) {
      try {
        const devices = await SpotifyService.getAvailableDevices();
        setAvailableSpotifyDevices(devices);
        setSpotifyDevice(device.speaker || '');
      } catch (error) {
        console.error('Erreur lors de la récupération des appareils Spotify:', error);
      }
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedDevice) return;

    try {
      const config = {
        volume,
        spotify_device: spotifyDevice,
        time_slot: isTimeRestricted
          ? {
              enabled: true,
              start_time: startTime ? startTime.format('HH:mm') : null,
              end_time: endTime ? endTime.format('HH:mm') : null,
            }
          : {
              enabled: false,
              start_time: null,
              end_time: null,
            },
        nickname: deviceNickname,
      };

      await deviceService.updateDeviceConfig(selectedDevice.id, config);
      setConfigDialogOpen(false);
      fetchDevices(); // Rafraîchir la liste des appareils
    } catch (error) {
      console.error('Erreur lors de la sauvegarde de la configuration:', error);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'background.default',
        }}
      >
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <PageTransition>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          py: { xs: 3, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          {/* Header Section */}
          <Box
            sx={{
              mb: { xs: 3, md: 6 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              gap: 2,
            }}
          >
            <Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1,
                  fontSize: { xs: '1.75rem', sm: '2.25rem', md: '3rem' },
                }}
              >
                Tableau de bord
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Gérez vos cartes de lecture et vos appareils
              </Typography>
            </Box>
            {!isSpotifyConnected && (
              <Button
                variant="contained"
                onClick={handleConnectSpotify}
                startIcon={<DevicesIcon />}
                sx={{
                  borderRadius: '50px',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                  },
                  alignSelf: { xs: 'flex-start', md: 'auto' },
                  width: { xs: '100%', sm: 'auto' },
                  mt: { xs: 1, md: 0 },
                }}
              >
                Connecter Spotify
              </Button>
            )}
          </Box>

          {/* Cards Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              mb: { xs: 3, md: 4 },
              borderRadius: { xs: 2, md: 4 },
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CreditCardIcon sx={{ mr: 2, color: 'primary.main', fontSize: { xs: 24, md: 32 } }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.15rem', md: '1.5rem' },
                }}
              >
                Mes cartes
              </Typography>
            </Box>
            <List>
              {cards.map((card) => (
                <ListItem
                  key={card.id}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    transition: 'all 0.3s',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    position: 'relative',
                    gap: { xs: 2, sm: 0 },
                    p: { xs: 2, sm: 2 },
                    '& .MuiListItemSecondaryAction-root': {
                      position: 'static',
                      transform: 'none',
                    },
                  }}
                  secondaryAction={
                    <Button
                      startIcon={<EditIcon />}
                      onClick={() => handleEditClick(card)}
                      sx={{
                        borderRadius: '50px',
                        px: { xs: 2, md: 3 },
                        py: 1,
                        color: 'primary.main',
                        borderColor: 'primary.main',
                        '&:hover': {
                          bgcolor: 'primary.main',
                          color: 'white',
                        },
                        position: { xs: 'absolute', sm: 'relative' },
                        top: { xs: 8, sm: 'auto' },
                        right: { xs: 8, sm: 16 },
                      }}
                    >
                      Modifier
                    </Button>
                  }
                >
                  <ListItemIcon
                    sx={{
                      mr: { xs: 0, sm: 2 },
                      minWidth: { xs: 'auto', sm: 56 },
                      alignSelf: { xs: 'center', sm: 'flex-start' },
                    }}
                  >
                    {spotifyCovers[card.id] ? (
                      <Avatar
                        src={spotifyCovers[card.id]}
                        variant="rounded"
                        sx={{ width: 56, height: 56 }}
                      />
                    ) : (
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: 'primary.main',
                        }}
                      >
                        <CreditCardIcon />
                      </Avatar>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          textAlign: { xs: 'center', sm: 'left' },
                        }}
                      >
                        Carte {card.rfid_id}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textAlign: { xs: 'center', sm: 'left' } }}
                      >
                        {card.url ? (
                          <Box
                            component="a"
                            href={getSpotifyUrl(card.url)}
                            target="_blank"
                            rel="noopener"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              width: { xs: '100%', sm: '100%', md: '300px' },
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {card.url}
                          </Box>
                        ) : (
                          'Aucune URL configurée'
                        )}
                      </Typography>
                    }
                    sx={{ width: '100%' }}
                  />
                </ListItem>
              ))}
            </List>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                variant="contained"
                onClick={() => navigate('/store')}
                startIcon={<CreditCardIcon />}
                sx={{
                  borderRadius: '50px',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                  '&:hover': {
                    boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                  },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                Acheter une carte
              </Button>
            </Box>
          </Paper>

          {/* Devices Section */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3, md: 4 },
              borderRadius: { xs: 2, md: 4 },
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <DevicesIcon sx={{ mr: 2, color: 'primary.main', fontSize: { xs: 24, md: 32 } }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.15rem', md: '1.5rem' },
                }}
              >
                Mes appareils
              </Typography>
            </Box>
            <List>
              {devices.map((device) => {
                const lastSeen = new Date(device.last_seen);
                const online = lastSeen > new Date(Date.now() - 1000 * 120);
                return (
                  <ListItem
                    key={device.id}
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: 'background.default',
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                      transition: 'all 0.3s',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'center', sm: 'center' },
                      gap: { xs: 2, sm: 0 },
                      p: { xs: 2, sm: 2 },
                      position: 'relative',
                      '& .MuiListItemSecondaryAction-root': {
                        position: 'static',
                        transform: 'none',
                      },
                    }}
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          startIcon={<DeleteIcon />}
                          onClick={() => handleDeleteDeviceClick(device)}
                          sx={{
                            borderRadius: '50px',
                            px: { xs: 2, md: 3 },
                            py: 1,
                            color: 'error.main',
                            borderColor: 'error.main',
                            '&:hover': {
                              bgcolor: 'error.main',
                              color: 'white',
                            },
                            position: { xs: 'absolute', sm: 'relative' },
                            top: { xs: 8, sm: 'auto' },
                            right: { xs: 8, sm: 16 },
                          }}
                        >
                          Supprimer
                        </Button>
                        <Button
                          startIcon={<EditIcon />}
                          onClick={() => handleOpenConfig(device)}
                          sx={{
                            borderRadius: '50px',
                            px: { xs: 2, md: 3 },
                            py: 1,
                            color: 'primary.main',
                            borderColor: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                            },
                            position: { xs: 'absolute', sm: 'relative' },
                            top: { xs: 8, sm: 'auto' },
                            right: { xs: 8, sm: 16 },
                          }}
                        >
                          Configurer
                        </Button>
                      </Box>
                    }
                  >
                    <ListItemIcon
                      sx={{
                        mr: { xs: 0, sm: 2 },
                        minWidth: { xs: 'auto', sm: 56 },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: online ? 'success.main' : 'error.main',
                        }}
                      >
                        <DevicesIcon />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            textAlign: { xs: 'center', sm: 'left' },
                          }}
                        >
                          {device.nickname ? device.nickname : device.name}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          component="div"
                          color="text.secondary"
                          sx={{ textAlign: { xs: 'center', sm: 'left' } }}
                        >
                          {online ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'success.main',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                              }}
                            >
                              <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
                              En ligne
                            </Box>
                          ) : (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'error.main',
                                justifyContent: { xs: 'center', sm: 'flex-start' },
                              }}
                            >
                              <ErrorIcon sx={{ mr: 1, fontSize: 16 }} />
                              Hors ligne
                            </Box>
                          )}
                          Dernière connexion : {formatLastSeen(device.last_seen)}
                        </Typography>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: { xs: 'center', sm: 'flex-end' },
                mt: 3,
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<HelpOutlineIcon />}
                onClick={() => setDeviceGuideOpen(true)}
                sx={{
                  borderRadius: '50px',
                  px: { xs: 3, md: 4 },
                  py: { xs: 1, md: 1.5 },
                  fontSize: { xs: '0.95rem', md: '1.1rem' },
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  '&:hover': {
                    borderColor: 'primary.dark',
                    bgcolor: 'rgba(255, 107, 107, 0.05)',
                  },
                  width: { xs: '100%', sm: 'auto' },
                  order: { xs: 2, sm: 1 },
                }}
              >
                Mode d'emploi
              </Button>
            </Box>
          </Paper>
        </Container>

        {/* Edit Dialog */}
        <Dialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: { xs: 2, md: 3 },
              p: { xs: 1, md: 0 },
              mx: { xs: 2, md: 0 },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography
              variant="h5"
              component="p"
              sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Modifier la carte
            </Typography>
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="URL Spotify"
              type="text"
              fullWidth
              value={newUrl}
              onChange={(e) => setNewUrl(e.target.value)}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
            <Button
              onClick={() => setEditDialogOpen(false)}
              sx={{
                borderRadius: '50px',
                px: 3,
                py: 1,
                color: 'text.secondary',
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              sx={{
                borderRadius: '50px',
                px: 3,
                py: 1,
                boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                '&:hover': {
                  boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                },
              }}
            >
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Device Guide Modal */}
        {deviceGuideOpen && (
          <Dialog
            open={deviceGuideOpen}
            onClose={() => setDeviceGuideOpen(false)}
            maxWidth="lg"
            fullWidth
            scroll="body"
            sx={{
              '& .MuiDialog-paper': {
                borderRadius: { xs: 2, md: 2 },
                mx: { xs: 2, md: 3 },
                overflow: 'hidden',
                width: { xs: 'calc(100% - 32px)', md: '100%' },
              },
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, type: 'spring', stiffness: 300, damping: 30 }}
            >
              <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <DevicesIcon
                    sx={{ mr: 2, color: 'primary.main', fontSize: { xs: 24, md: 28 } }}
                  />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                    }}
                  >
                    Ajouter un nouvel appareil Jukobox (3 minutes chrono)
                  </Typography>
                </Box>
              </DialogTitle>
              <DialogContent>
                <Box sx={{ mt: 2 }}>
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1, duration: 0.3 }}
                  >
                    <Typography
                      variant="body1"
                      color="primary.main"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        color: 'primary.main',
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      Avant de commencer
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 16, mr: 1 }} />
                      <span>
                        Gardez votre téléphone chargé et connecté à votre réseau Wi-Fi domestique.
                      </span>
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 16, mr: 1 }} />
                      <span>
                        Assurez-vous d’avoir installé l’application gratuite Jukobox (App Store /
                        Google Play).
                      </span>
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center' }}
                    >
                      <CheckCircleIcon sx={{ color: 'primary.main', fontSize: 16, mr: 1 }} />
                      <span>
                        Branchez votre nouvelle Jukobox : le cercle lumineux clignote doucement,
                        signe qu’elle attend l’association.
                      </span>
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        mb: 2,
                        mt: 2,
                        color: 'primary.main',
                        fontSize: { xs: '1.1rem', md: '1.25rem' },
                      }}
                    >
                      Étapes pour ajouter un nouvel appareil
                    </Typography>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2, duration: 0.3 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        1. Lancez l’application
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Ouvrez Jukobox et touchez “Ajouter un appareil”. L’application cherche
                        aussitôt les boîtiers à proximité.
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3, duration: 0.3 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        2. Sélectionnez votre Jukobox
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Un nom du type “Jukobox-XXXX” apparaît ; touchez-le pour poursuivre. La
                        connexion s’établit en quelques secondes.
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.4, duration: 0.3 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        3. Partagez votre Wi-Fi
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Votre téléphone propose automatiquement votre réseau domestique ; validez,
                        puis saisissez votre mot de passe. La Jukobox se connecte et le cercle
                        lumineux devient fixe : elle est en ligne.
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        4. Choisissez où le son sortira (version UNI)
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        La liste des enceintes Spotify Connect de la maison s’affiche. Sélectionnez
                        celle qui se trouve dans la pièce de l’enfant (vous pourrez en changer à
                        tout moment).
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        5. Personnalisez (facultatif)
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        - Renommez la Jukobox : “Chambre de Lila”, par exemple.
                        <br />
                        - Attribuez-lui une couleur de halo pour la reconnaître du premier coup
                        d’œil.
                        <br />- Activez les plages horaires de silence si vous le souhaitez (sieste,
                        nuit).
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        6. Découvrez la Jukobox
                      </Typography>
                      <Typography variant="body1" color="text.secondary">
                        Passez une carte “démo” : la chanson démarre aussitôt sur l’enceinte
                        choisie. La configuration est terminée !
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5, duration: 0.3 }}
                  >
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" color="text.secondary">
                        Astuce : Pour ajouter d’autres Jukobox ou changer d’enceinte, retournez dans
                        le menu Appareils de l’application ; tout se fait en deux tapotements. Votre
                        enfant n’a plus qu’à collectionner les cartes et laisser la musique
                        l’emporter.
                      </Typography>
                    </Box>
                  </motion.div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.3 }}
                  >
                    <Box sx={{ mt: 4, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                      <Typography variant="body2" color="info.contrastText">
                        <strong>Besoin d'aide ?</strong> Si vous rencontrez des difficultés lors de
                        l'installation, n'hésitez pas à contacter notre support client.
                      </Typography>
                    </Box>
                  </motion.div>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
                <Button
                  onClick={() => setDeviceGuideOpen(false)}
                  sx={{
                    borderRadius: '50px',
                    px: 3,
                    py: 1,
                    color: 'text.secondary',
                  }}
                >
                  Fermer
                </Button>
              </DialogActions>
            </motion.div>
          </Dialog>
        )}

        {/* Delete Device Dialog */}
        <Dialog
          open={deleteDeviceDialogOpen}
          onClose={() => setDeleteDeviceDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: { xs: 2, md: 3 },
              p: { xs: 1, md: 0 },
              mx: { xs: 2, md: 0 },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography
              variant="h5"
              component="p"
              sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Supprimer l'appareil
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Êtes-vous sûr de vouloir supprimer l'appareil{' '}
              <strong>
                {selectedDevice?.nickname ? selectedDevice?.nickname : selectedDevice?.name}
              </strong>{' '}
              ? Cette action est irréversible.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
            <Button
              onClick={() => setDeleteDeviceDialogOpen(false)}
              sx={{
                borderRadius: '50px',
                px: 3,
                py: 1,
                color: 'text.secondary',
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleDeleteDevice}
              variant="contained"
              color="error"
              sx={{
                borderRadius: '50px',
                px: 3,
                py: 1,
                boxShadow: '0 4px 14px 0 rgba(244, 67, 54, 0.2)',
                '&:hover': {
                  boxShadow: '0 6px 20px 0 rgba(244, 67, 54, 0.3)',
                },
              }}
            >
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Configuration Dialog */}
        <Dialog
          open={configDialogOpen}
          onClose={() => setConfigDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          sx={{
            '& .MuiDialog-paper': {
              borderRadius: { xs: 2, md: 3 },
              p: { xs: 1, md: 0 },
              mx: { xs: 2, md: 0 },
            },
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography
              variant="h5"
              component="p"
              sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Configuration de {selectedDeviceConfig?.name}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Section Volume */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Volume
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VolumeDown />
                  <Slider
                    value={volume}
                    onChange={(_, value) => setVolume(value as number)}
                    min={0}
                    max={100}
                    valueLabelDisplay="auto"
                    sx={{ flex: 1 }}
                  />
                  <VolumeUp />
                </Box>
              </Box>

              {/* Section Surnom */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Surnom de l'appareil
                </Typography>
                <TextField
                  fullWidth
                  value={deviceNickname}
                  onChange={(e) => setDeviceNickname(e.target.value)}
                  placeholder="Entrez un surnom pour l'appareil"
                  variant="outlined"
                />
              </Box>

              {selectedDeviceConfig?.name === 'JUKOBOX-UNI' && (
                <>
                  {/* Section Spotify */}
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Appareil Spotify
                    </Typography>
                    {isSpotifyConnected ? (
                      <FormControl fullWidth>
                        <InputLabel id="spotify-device-label">Appareil de lecture</InputLabel>
                        <Select
                          labelId="spotify-device-label"
                          value={spotifyDevice}
                          label="Appareil de lecture"
                          onChange={(e) => setSpotifyDevice(e.target.value)}
                          sx={{
                            borderRadius: 2,
                          }}
                        >
                          {availableSpotifyDevices.map((device) => (
                            <MenuItem key={device.id} value={device.id}>
                              {device.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'info.light',
                          borderRadius: 2,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <HelpOutlineIcon sx={{ color: 'info.main' }} />
                        <Typography variant="body2" color="info.contrastText">
                          Connectez-vous à Spotify pour voir la liste des appareils disponibles
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </>
              )}

              {/* Section Plages horaires */}
              <Box>
                <Typography variant="h6" gutterBottom>
                  Plages horaires de lecture
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isTimeRestricted}
                      onChange={(e) => setIsTimeRestricted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Activer les plages horaires"
                  sx={{ mb: 2 }}
                />
                {isTimeRestricted && (
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                    <Box
                      sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}
                    >
                      <TimePicker
                        label="Heure de début"
                        value={startTime}
                        onChange={(newValue) => setStartTime(newValue)}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                        slotProps={{
                          textField: {
                            placeholder: 'HH:mm',
                          },
                        }}
                        format="HH:mm"
                      />
                      <TimePicker
                        label="Heure de fin"
                        value={endTime}
                        onChange={(newValue) => setEndTime(newValue)}
                        sx={{
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                        slotProps={{
                          textField: {
                            placeholder: 'HH:mm',
                          },
                        }}
                        format="HH:mm"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      L'appareil ne fonctionnera que pendant cette plage horaire. En dehors de ces
                      heures, il sera automatiquement désactivé.
                    </Typography>
                  </LocalizationProvider>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: { xs: 2, md: 3 } }}>
            <Button
              onClick={() => setConfigDialogOpen(false)}
              sx={{
                borderRadius: '50px',
                px: 3,
                py: 1,
                color: 'text.secondary',
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSaveConfig}
              variant="contained"
              sx={{
                borderRadius: '50px',
                px: 3,
                py: 1,
                boxShadow: '0 4px 14px 0 rgba(255, 107, 107, 0.2)',
                '&:hover': {
                  boxShadow: '0 6px 20px 0 rgba(255, 107, 107, 0.3)',
                },
              }}
            >
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ bottom: { xs: 16, sm: 24 } }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity="error"
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 4px 14px 0 rgba(0, 0, 0, 0.1)',
            }}
          >
            {error && Object.values(error)[0]}
          </Alert>
        </Snackbar>
      </Box>
    </PageTransition>
  );
};

export default UserDashboard;
